import { IonContent, IonPage } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { Events, Screens, useAnalytics } from "../AppAnalyticsProvider";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";

const CONTACT_FORM_URL = process.env.REACT_APP_CONTACT_FORM_URL
    || "https://us-central1-intense-signer-264404.cloudfunctions.net/contactForm";

const DELETION_REASONS = [
    "I no longer use the app",
    "Privacy concerns",
    "I have another account",
    "I'm not satisfied with the experience",
    "Other",
];

const inputClass = "w-full bg-white/[0.06] border border-white/[0.12] rounded-lg px-4 py-3 text-white text-[15px] font-['Mukta_Mahee'] placeholder:text-white/30 focus:outline-none focus:border-highlight/60 focus:ring-1 focus:ring-highlight/30 transition-colors duration-200";
const labelClass = "block text-[13px] font-bold text-white/80 tracking-[0.3px] mb-2";

type Status = "idle" | "submitting" | "success" | "error";

const DeleteAccountPage: React.FC = () => {
    const history = useHistory();
    const { trackScreen, trackEvent } = useAnalytics();

    // Track screen view and fire the churn-signal event on mount.
    // This surfaces on the errors / churn dashboard panels.
    useEffect(() => {
        trackScreen(Screens.DELETE_ACCOUNT, 'DeleteAccountPage');
        trackEvent(Events.DELETE_ACCOUNT_INITIATED, {});
    }, []);

    const [email, setEmail] = useState("");
    const [username, setUsername] = useState("");
    const [reason, setReason] = useState("");
    const [message, setMessage] = useState("");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [website, setWebsite] = useState(""); // honeypot

    const [status, setStatus] = useState<Status>("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleBack = () => {
        if (history.length > 1) history.goBack();
        else history.push("/");
    };

    const isValid = () =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        reason.length > 0 &&
        confirmDelete;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (website) return; // honeypot silently
        if (!isValid()) {
            setErrorMessage("Please fill in all required fields and confirm account deletion.");
            setStatus("error");
            return;
        }

        setStatus("submitting");
        setErrorMessage("");

        try {
            const response = await fetch(CONTACT_FORM_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    formType: "delete",
                    email: email.trim(),
                    username: username.trim(),
                    reason,
                    message: message.trim(),
                    website,
                }),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({ error: "Submission failed" }));
                if (response.status === 429) {
                    throw new Error("You've submitted too many requests. Please try again in an hour.");
                }
                throw new Error(data.error || "Something went wrong. Please try again.");
            }

            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMessage(err instanceof Error ? err.message : "Something went wrong.");
        }
    };

    return (
        <IonPage>
            <CustomHelmet
                title="Delete Account — Ecliptic"
                description="Request deletion of your Ecliptic account and associated data."
                url="https://ecliptic.day/delete"
            />
            <IonContent>
                <div className="min-h-full bg-black text-white font-['Mukta_Mahee']">
                    <nav className="sticky top-0 z-50 bg-[rgba(0,0,0,0.75)] backdrop-blur-[24px] border-b border-[rgba(88,97,172,0.18)]">
                        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-white/65 hover:text-white text-sm font-bold tracking-[0.4px] transition-colors duration-200"
                                aria-label="Go back"
                            >
                                ← Back
                            </button>
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[26px] w-auto object-contain block"
                            />
                        </div>
                    </nav>

                    <article className="max-w-2xl mx-auto px-6 py-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
                            Delete Account
                        </h1>
                        <p className="text-white/65 text-[15px] leading-[1.6] mb-4">
                            We're sorry to see you go. Use this form to request deletion of your Ecliptic account
                            and all associated data.
                        </p>
                        <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4 mb-10">
                            <p className="text-white/50 text-[14px] leading-[1.6] m-0">
                                <strong className="text-white/70">What happens when you request deletion:</strong>
                            </p>
                            <ul className="text-white/50 text-[14px] leading-[1.6] mt-2 mb-0 pl-4 list-disc space-y-1">
                                <li>Your profile, trick videos, and all associated content will be permanently removed</li>
                                <li>Your Bisk balance and transaction history will be deleted</li>
                                <li>Any active subscriptions should be cancelled separately through your app store</li>
                                <li>Account deletion is permanent and cannot be undone</li>
                                <li>Deletion requests are processed within 30 days</li>
                            </ul>
                        </div>

                        {status === "success" ? (
                            <div className="bg-highlight/10 border border-highlight/30 rounded-lg p-6">
                                <h2 className="text-xl font-bold mb-2">Deletion request received.</h2>
                                <p className="text-white/70 text-[15px]">
                                    We've received your account deletion request. You'll receive a confirmation email
                                    at the address you provided. Your account and data will be deleted within 30 days.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => history.push("/")}
                                    className="mt-4 px-6 py-2 bg-highlight text-white font-bold text-[14px] rounded-lg hover:bg-highlight/80 transition-colors"
                                >
                                    Back to Home
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                                <div>
                                    <label htmlFor="delete-email" className={labelClass}>
                                        Email Address Associated with Your Account <span className="text-highlight-danger">*</span>
                                    </label>
                                    <input
                                        id="delete-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="The email you signed up with"
                                        className={inputClass}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="delete-username" className={labelClass}>
                                        Ecliptic Username
                                    </label>
                                    <input
                                        id="delete-username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="Your username (helps us locate your account)"
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="delete-reason" className={labelClass}>
                                        Reason for Leaving <span className="text-highlight-danger">*</span>
                                    </label>
                                    <select
                                        id="delete-reason"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        className={`${inputClass} appearance-none cursor-pointer`}
                                        required
                                        aria-required="true"
                                        style={{
                                            backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23ffffff80' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: "no-repeat",
                                            backgroundPosition: "right 16px center",
                                            paddingRight: "40px",
                                        }}
                                    >
                                        <option value="" disabled>Select a reason...</option>
                                        {DELETION_REASONS.map((r) => (
                                            <option key={r} value={r} className="bg-black text-white">{r}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="delete-message" className={labelClass}>
                                        Additional Comments
                                    </label>
                                    <textarea
                                        id="delete-message"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Anything else you'd like us to know..."
                                        rows={4}
                                        className={`${inputClass} resize-y min-h-[100px]`}
                                    />
                                </div>

                                <div className="flex items-start gap-3">
                                    <input
                                        id="delete-confirm"
                                        type="checkbox"
                                        checked={confirmDelete}
                                        onChange={(e) => setConfirmDelete(e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-white/20 bg-white/[0.06] accent-highlight-danger cursor-pointer"
                                        required
                                        aria-required="true"
                                    />
                                    <label htmlFor="delete-confirm" className="text-[14px] text-white/70 cursor-pointer leading-[1.5]">
                                        I understand that account deletion is <strong className="text-highlight-danger">permanent</strong> and
                                        all my data, including trick videos, Bisk balance, and profile information will be
                                        irreversibly removed. <span className="text-highlight-danger">*</span>
                                    </label>
                                </div>

                                {/* Honeypot — hidden from real users */}
                                <input
                                    type="text"
                                    name="website"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    className="hidden"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />

                                {status === "error" && errorMessage && (
                                    <div className="text-highlight-danger text-[14px]" role="alert">
                                        {errorMessage}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    className="w-full sm:w-auto px-8 py-3 bg-highlight-danger text-white font-bold text-[15px] rounded-lg tracking-[0.3px] transition-all duration-200 hover:bg-highlight-danger/80 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {status === "submitting" ? "Submitting..." : "Request Account Deletion"}
                                </button>
                            </form>
                        )}
                    </article>

                    <footer className="max-w-3xl mx-auto px-6 py-10 border-t border-white/10 mt-12">
                        <div className="flex flex-wrap gap-6 text-sm text-white/50">
                            <a href="/invest" className="hover:text-white transition-colors">Invest & Partner</a>
                            <a href="/careers" className="hover:text-white transition-colors">Careers</a>
                            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
                            <a href="/eula" className="hover:text-white transition-colors">EULA</a>
                            <a href="/" className="hover:text-white transition-colors ml-auto">Home</a>
                        </div>
                    </footer>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default DeleteAccountPage;
